import React from 'react';
import { Box } from '@mui/material';
import LottoPosTile from '../../LottoPosTile';
import SummaryTile from '../../SummaryTile';
import TileCalculationPopover from '../../TileCalculationPopover';
import { ManualAmountInput } from '../components/ManualAmountInput';
import { TileCheckbox } from '../components/TileCheckbox';
import { VarianceBadge } from '../components/VarianceBadge';
import type { ManualPayoutTileConfig, TileConfig } from '../types';
import type { ManualDayEntryFormValues } from '../../manualDayEntry';
import { formatCurrency } from 'utils';

export function PayoutsTile({
  config,
  isLoading,
  isAllShift,
  isLoadingManualDayEntry,
  isSavingManualDayEntry,
  manualFormValues,
  onManualFieldChange,
  posInputValue,
  onPosInputChange,
  localVerified,
  localAck,
  onRequestDialog,
}: {
  config: TileConfig;
  isLoading: boolean;
  isAllShift: boolean;
  isLoadingManualDayEntry: boolean;
  isSavingManualDayEntry: boolean;
  manualFormValues: ManualDayEntryFormValues;
  onManualFieldChange: (field: keyof ManualDayEntryFormValues, nextValue: string) => void;
  posInputValue: string;
  onPosInputChange: (nextValue: string) => void;
  localVerified: boolean;
  localAck: boolean;
  onRequestDialog: (args: { mode: 'verify' | 'ack'; nextVerified: boolean; nextAck: boolean }) => void;
}) {
  if (!config.visible) return null;

  if (config.variant === 'manual_payouts') {
    const typed = config as ManualPayoutTileConfig;
    const showVariance = Boolean(typed.footer && String(typed.footer).startsWith('POS Total:'));
    const disabled = isLoadingManualDayEntry || isSavingManualDayEntry;

    return (
      <SummaryTile
        title="PAYOUTS"
        cardClassName={typed.cardClassName}
        value={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            {showVariance && <VarianceBadge variance={typed.variance} />}
            <ManualAmountInput
              ariaLabel="Payouts"
              value={manualFormValues.lottoPayoutsAmount}
              onChange={(next) => onManualFieldChange('lottoPayoutsAmount', next)}
              disabled={disabled}
            />
          </Box>
        }
        subtext={
          showVariance ? (
            <Box
              sx={{
                color: '#A0AEC0 !important',
                fontStyle: 'italic',
                fontSize: '12px',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
              }}
            >
              free scratchers {formatCurrency(typed.posFreeScratchersAmount)}({typed.posFreeScratchersCount})
            </Box>
          ) : undefined
        }
        footer={typed.footer}
        isLoading={isLoading || isLoadingManualDayEntry}
      />
    );
  }

  const shouldShowAction = config.variant !== 'standard_lotto_pos' || config.badge !== 'MATCHED';
  const action =
    config.actionLabel && shouldShowAction ? (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <TileCheckbox
            isAllShift={isAllShift}
            label={config.actionLabel}
            localVerified={localVerified}
            localAck={localAck}
            onRequestDialog={onRequestDialog}
          />
        </Box>
      </Box>
    ) : null;

  if (config.variant === 'standard_lotto_pos') {
    const tile = (
      <LottoPosTile
        title={config.title}
        badge={config.badge}
        value={config.value}
        subtext={config.subtext}
        footer={config.footer}
        isLoading={isLoading}
        cardClassName={config.cardClassName}
        extraContent={
          config.inputPlaceholder ? (
            <ManualAmountInput
              ariaLabel="Payout POS Data"
              value={posInputValue}
              onChange={onPosInputChange}
              disabled={false}
              placeholder={config.inputPlaceholder}
              width={150}
            />
          ) : undefined
        }
      >
        {action}
      </LottoPosTile>
    );

    if (config.popover?.calculations) {
      return (
        <TileCalculationPopover
          calculations={config.popover.calculations}
          showZeroDiscrepancyPopover={Boolean(config.popover.showZeroDiscrepancyPopover)}
        >
          {tile}
        </TileCalculationPopover>
      );
    }

    return tile;
  }

  return (
    <SummaryTile
      title={config.title}
      value={config.value || ''}
      subtext={config.subtext}
      footer={config.footer}
      isLoading={isLoading}
      className={config.className}
      valueClassName={config.valueClassName}
      cardClassName={config.cardClassName}
    >
      {action}
    </SummaryTile>
  );
}

